const STORAGE_KEY = 'alternate-date-selected';
const SELECTED_DATE_KEY = 'alternate-date-value';
const FOMO_STORAGE_KEY_PREFIX = 'delivery_urgency_';

class DeliveryUrgencyBar extends HTMLElement {
  constructor() {
    super();

    this.messages = this.querySelectorAll('[data-message]');
    this.skeleton = this.querySelector('[data-skeleton]');
    this.content = this.querySelector('[data-content]');
    this.minRange = parseInt(this.getAttribute('data-min-range') || '30');
    this.maxRange = parseInt(this.getAttribute('data-max-range') || '100');
    this.alwaysShow = this.getAttribute('data-always-show') === 'true';
    this.template = this.getAttribute('data-template') || '';
    this.autoRefreshInterval = parseInt(this.getAttribute('data-auto-refresh-interval') || '0');
    this.selectedDate = null;
    this.currentCount = null;
    this.currentStorageKey = null;
    this.refreshTimer = null;
  }

  connectedCallback() {
    this.checkVisibility();
    this.restoreSelectedDate();

    window.addEventListener('storage', this.onStorageChange.bind(this));
    document.addEventListener('alternate-date-selected', this.onAlternateDateSelected.bind(this));
    document.addEventListener('alternate-date-cleared', this.onAlternateDateCleared.bind(this));
    document.addEventListener('product:switched', this.onProductSwitched.bind(this));

    // If a cart item is removed, check if there are any cart items left and if not, handle the expired state
    document.addEventListener('cart-item-removed', (event) => {
      const cartItems = document.querySelectorAll('.cart-item');
      if (cartItems.length === 0) {
        this.hide();
      }
    })

    this.startAutoRefresh();
  }

  startAutoRefresh() {
    if (this.autoRefreshInterval > 0) {
      this.refreshTimer = setInterval(() => {
        this.applyLiveVariation();
      }, this.autoRefreshInterval * 1000);
    }
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  applyLiveVariation() {
    if (this.currentCount === null) return;

    const variation = this.getSmallVariation();
    let newCount = this.currentCount + variation;
    newCount = Math.max(this.minRange, Math.min(this.maxRange, newCount));
    this.currentCount = newCount;

    if (this.currentStorageKey) {
      this.saveToStorage(this.currentStorageKey, newCount);
    }

    this.updateMessage(newCount);
  }

  restoreSelectedDate() {
    const savedDate = sessionStorage.getItem(SELECTED_DATE_KEY);
    const isAlternateDateSelected = sessionStorage.getItem(STORAGE_KEY) === 'true';

    if (isAlternateDateSelected && savedDate) {
      this.selectedDate = savedDate;
      const viewerCount = this.getSmartViewerCount(savedDate);
      this.updateMessage(viewerCount);
    } else if (this.alwaysShow) {
      this.initViewerCount();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this.onStorageChange.bind(this));
    document.removeEventListener('alternate-date-selected', this.onAlternateDateSelected.bind(this));
    document.removeEventListener('alternate-date-cleared', this.onAlternateDateCleared.bind(this));
    document.removeEventListener('product:switched', this.onProductSwitched.bind(this));
    this.stopAutoRefresh();
  }

  checkVisibility() {
    if (this.alwaysShow) {
      this.show();
      return;
    }

    const isAlternateDateSelected = sessionStorage.getItem(STORAGE_KEY) === 'true';
    if (isAlternateDateSelected) {
      this.show();
    } else {
      this.hide();
    }
  }

  onStorageChange(event) {
    if (event.key === STORAGE_KEY) {
      this.checkVisibility();
    }
  }

  onAlternateDateSelected(event) {
    const date = event.detail?.date;
    if (date) {
      this.selectedDate = date;
      sessionStorage.setItem(SELECTED_DATE_KEY, date);
      const viewerCount = this.getSmartViewerCount(date);
      this.updateMessage(viewerCount);
    }

    if (!this.alwaysShow) {
      this.show();
    }
  }

  onAlternateDateCleared() {
    this.selectedDate = null;
    sessionStorage.removeItem(SELECTED_DATE_KEY);
    if (!this.alwaysShow) {
      this.hide();
    }
  }

  onProductSwitched(event) {
    if (this.template === 'product' && this.alwaysShow) {
      const variantId = event.detail?.productId;
      const viewerCount = this.getSmartViewerCount(variantId);
      this.updateMessage(viewerCount);
    }
  }

  show() {
    this.style.display = 'block';
  }

  hide() {
    this.style.display = 'none';
  }

  initViewerCount() {
    const viewerCount = this.getSmartViewerCount();
    this.updateMessage(viewerCount);
  }

  getStorageKey(date) {
    if (date) {
      return `${FOMO_STORAGE_KEY_PREFIX}${date}`;
    }
    return `${FOMO_STORAGE_KEY_PREFIX}global`;
  }

  getSmartViewerCount(date) {
    const storageKey = this.getStorageKey(date);
    this.currentStorageKey = storageKey;
    const now = Date.now();
    const stored = sessionStorage.getItem(storageKey);

    if (stored) {
      try {
        const data = JSON.parse(stored);
        const timeDiff = now - data.timestamp;
        const secondsPassed = Math.floor(timeDiff / 1000);

        if (secondsPassed < 30) {
          return data.count;
        }

        const minutesPassed = Math.floor(timeDiff / (1000 * 60));
        if (minutesPassed < 30) {
          const variation = this.getSmallVariation();
          let newCount = data.count + variation;
          newCount = Math.max(this.minRange, Math.min(this.maxRange, newCount));
          this.saveToStorage(storageKey, newCount);
          return newCount;
        }
      } catch (e) {
        console.warn('DeliveryUrgencyBar: Error parsing stored data', e);
      }
    }

    const initialCount = this.getRandomInRange(this.minRange, this.maxRange);
    this.saveToStorage(storageKey, initialCount);
    return initialCount;
  }

  getSmallVariation() {
    const maxVariation = 5;
    return Math.floor(Math.random() * (maxVariation * 2 + 1)) - maxVariation;
  }

  getRandomInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  saveToStorage(storageKey, count) {
    const data = {
      count: count,
      timestamp: Date.now()
    };
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }

  updateMessage(count) {
    this.currentCount = count;

    if (this.messages) {
      this.messages.forEach(message => {
        message.textContent = count;
      });

      if (this.skeleton && this.content) {
        this.skeleton.style.display = 'none';
        this.content.style.display = 'inline-flex';
      }
    }
  }
}

if (!customElements.get('delivery-urgency-bar')) {
  customElements.define('delivery-urgency-bar', DeliveryUrgencyBar);
}
